import React, { useEffect, useState, useRef, useImperativeHandle } from 'react';
import { showToast } from '../../../stores/ToastStore';

import {
  useGetDocuments,
  useGetSignedUrls,
} from '../../../utils/DocumentsHooks';
import { DOCUMENT_STATUSES } from '../documents/Document';
import LetterAvatar from './LetterAvatar';

export const Avatar = React.forwardRef(
  (
    {
      userId,
      altName,
      className = null,
      style = null,
      overlapping = false,
      useLetters = false,
      user,
    },
    ref,
  ) => {
    const [signedUrl, setSignedUrl] = useState(
      'https://assets.cargomatic.com/default.jpg',
    );
    const [showLetters, setShowLetters] = useState(false);
    const [pendingOverlay, setPendingOverlay] = useState(false);
    const [oldDocument, setOldDocument] = useState(null);
    const {
      data: documentData,
      error: documentError,
      refetch: documentsRefetch,
    } = useGetDocuments({
      foreignKeys: [userId || ''],
      type: 'profile_picture',
      options: {
        order: {
          field: 'createdAt',
          direction: 'desc',
        },
        limit: 1,
      },
    });
    const {
      data: signedUrlsData,
      error: signedUrlsError,
      refetch: signedUrlsRefetch,
    } = useGetSignedUrls({
      foreignKeys: [userId || ''], // use single when endpoint could search by foreightKey
    });

    const refetchTimeoutRef = useRef(0);

    useEffect(() => {
      if (documentError) {
        showToast(
          'Get Avatar Error',
          `Get Avatar failed with error: ${documentError}`,
          'error',
        );
      }

      if (signedUrlsError) {
        showToast(
          'Get Avatar SignedUrl Error',
          `Get Avatar SignedUrl failed with error: ${signedUrlsError}`,
          'error',
        );
      }

      const documents = documentData?.documents.map((document) => {
        const signedItem = signedUrlsData?.signedUrls?.find(
          (signedUrl) => signedUrl.id === document.id,
        );

        return {
          ...document,
          signedUrl: signedItem?.signedUrl,
        };
      });

      const hasPendingDocuments = !!documentData?.documents.find(
        (document) => document.status === DOCUMENT_STATUSES.PENDING,
      );

      if (documents?.length) {
        setOldDocument(documents[0]);
      }

      if (!hasPendingDocuments) {
        setShowLetters(useLetters);
        if (documents && documents[0]?.signedUrl) {
          setSignedUrl(documents[0]?.signedUrl);
          setShowLetters(false);
        }
        setPendingOverlay(false);
      } else {
        setPendingOverlay(true);
        clearTimeout(refetchTimeoutRef.current);

        refetchTimeoutRef.current = setTimeout(() => {
          refetchTimeoutRef.current = 0;
          documentsRefetch({
            query: {
              foreignKeys: [userId || ''],
              type: 'profile_picture',
            },
            options: {
              order: {
                field: 'createdAt',
                direction: 'desc',
              },
            },
          });
          signedUrlsRefetch({
            query: {
              foreignKeys: [userId || ''],
            },
          });
        }, 10000);
      }
    }, [documentData, documentError, signedUrlsData, signedUrlsError]);

    useImperativeHandle(ref, () => ({
      refetchAvatar() {
        documentsRefetch();
        signedUrlsRefetch();
      },
      getOldDocument() {
        return oldDocument;
      },
    }));

    return !showLetters ? (
      <div className="relative">
        <img
          className={`${className} ${overlapping ? '-mr-2' : ''}`}
          src={signedUrl}
          alt={altName}
          style={style}
        />
        {pendingOverlay && (
          <div className="absolute top-0 left-0 flex justify-center items-center w-full h-full bg-green-transparent text-white text-xl">
            Uploading...
          </div>
        )}
      </div>
    ) : (
      <LetterAvatar user={user} overlapping={overlapping} />
    );
  },
);
